import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import queryString from 'querystring';
import {
  Modal,
  Row,
  Col,
  Spin,
  Button,
} from 'antd';
import ReactSelect, { components } from 'react-select';
import SimpleReactValidator from 'simple-react-validator';
import { withRouter } from 'react-router-dom';
import { transferApprovalActions } from 'store/actions';
import { SpH5, SpText } from 'components/DesignKit';
import EditableCurrencyInput from '../EditableCurrencyInput';


type Props = {
  showAddModal: Boolean,
  setShowAddModal: Function,
  selectedAccount: Object,
  loading: Boolean,
  teamMembers: Array,
  fetchTeamMembers: Function,
  addTransferApprovalRule: Function,
  editMode: Boolean,
  setEditMode: Function,
  ruleGid: String,
  fetchExistingRuleDetail: Function,
  existingRuleDetails: {
    amount: Number,
    approvedMembers: Array,
  },
  location: {
    search: String,
  }
};

const AddRule = (props: Props) => {
  const {
    showAddModal,
    setShowAddModal,
    selectedAccount,
    loading,
    teamMembers,
    fetchTeamMembers,
    addTransferApprovalRule,
    editMode,
    setEditMode,
    ruleGid,
    fetchExistingRuleDetail,
    existingRuleDetails,
    location,
  } = props;

  const [amount, setAmount] = useState(0);
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [, forceStateUpdate] = useState(0);

  const customValidator = {
    validators: {
      optionSelected: {
        message: 'Please select option(s).',
        required: true,
        rule: (val) => {
          if (Array.isArray(val)) {
            return val.length > 0;
          }
          return typeof val === 'object' && Object.keys(val).length === 2;
        },
      },
      amountCheck: {
        message: 'Please Enter a Valid Amount.',
        required: true,
        rule: (val) => {
          if (!Number.isNaN(Number(val)) && Number(val) > 0) {
            return true;
          }
          return false;
        },
      },
    },
  };

  const validator = useRef(new SimpleReactValidator(customValidator));

  const approvedMemberOptions = useMemo(() => teamMembers, [teamMembers]);

  useEffect(() => {
    fetchTeamMembers({});
  }, []);

  // if in edit mode, then fetch the
  // existing details and set them in the local variable
  useEffect(() => {
    if (!editMode) {
      return;
    }
    fetchExistingRuleDetail({
      gid: ruleGid,
    });
  }, [editMode]);

  useEffect(() => {
    if (!editMode) {
      return;
    }
    setAmount(existingRuleDetails.amount);
    setApprovedMembers(existingRuleDetails.approvedMembers);
  }, [editMode, existingRuleDetails]);

  const handleAddRule = () => {
    if (validator.current.allValid()) {
      // send value to the api
      addTransferApprovalRule({
        gid: editMode ? ruleGid : null,
        editMode,
        amount: editMode ? (existingRuleDetails?.amount !== amount ? amount : null) : amount,
        approvedMembers: approvedMembers ?? [],
        fetchRulesParams: queryString.parse(location.search),
      });
      setEditMode(false);
      setShowAddModal(false);
    } else {
      validator.current.showMessages();
      forceStateUpdate(state => (state === 0 ? state + 1 : state - 1));
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setShowAddModal(false);
  };

  const handleRemoveAllMembers = () => {
    setApprovedMembers([]);
  };

  // React select styling
  const antDV3SelectStyles = {
    container: (base) => ({
      ...base,
      width: '100%',
    }),
    option: (base) => ({
      ...base,
      height: '40px',
      lineHeight: '40px',
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      alignItems: 'center',
    }),
    control: (base, state) => ({
      ...base,
      width: '100%',
      height: '40px',
      minHeight: '40px',
      borderRadius: 4,
      borderColor: state.isFocused ? '#40a9ff' : '#d9d9d9',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#40a9ff',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px',
      overflowY: 'auto',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: 'rgba(0, 0, 0, 0.45)', // AntD placeholder color
      fontSize: '14px',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '14px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '40px',
    }),
  };

  const tallSelectStyles = {
    ...antDV3SelectStyles,
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? 'transparent' : state.isFocused ? '#f5f5f5' : 'transparent',
      color: 'inherit',
      cursor: 'pointer',
    }),
    control: (base, state) => ({
      ...antDV3SelectStyles.control(base, state),
      height: '80px',
      minHeight: '80px',
    }),
    valueContainer: (base) => ({
      ...antDV3SelectStyles.valueContainer(base),
      height: '80px',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start', // push chips to top
      paddingTop: '4px', // optional: space from top
      paddingBottom: '0px',
    }),
    placeholder: (base) => ({
      ...antDV3SelectStyles.valueContainer(base),
      color: 'rgba(0, 0, 0, 0.45)', // AntD placeholder color
      fontSize: '14px',
      height: '32px',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start', // push chips to top
      paddingTop: '4px', // optional: space from top
      paddingBottom: '0px',
    }),
    indicatorsContainer: (base) => ({
      ...antDV3SelectStyles.indicatorsContainer(base),
      height: '80px',
    }),
  };

  // Custom option with checkbox
  const CheckboxOption = (checkBoxProps) => (
    <components.Option {...checkBoxProps}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
      >
        <SpText>{checkBoxProps.label}</SpText>
        <input
          type="checkbox"
          checked={checkBoxProps.isSelected}
          onChange={() => null}
          style={{ marginRight: 8 }}
        />
      </div>
    </components.Option>
  );

  return (
    <Modal
      visible={showAddModal}
      maskClosable={!loading}
      closable={!loading}
      title="Add Approval Rules"
      onCancel={handleCancel}
      footer={[
        <Button
          type="secondary"
          shape="round"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>,
        <Button
          type="primary"
          shape="round"
          onClick={handleAddRule}
        >
          Set
        </Button>,
      ]}
    >
      <Spin
        spinning={loading}
        tip="Please wait..."
        size="large"
        delay={300}
      >
        <div className="p-4">
          <Row gutter={16} className="mt-2 mb-4">
            <Col span={24}>
              <SpH5>Amount</SpH5>
              <EditableCurrencyInput
                name="transferApprovalAmount"
                readOnly="false"
                valueInCents={amount}
                onChange={setAmount}
                currencyPrefix={selectedAccount?.currency?.prefix}
                className="mt-1"
              />
              <section style={{ color: 'red' }}>
                { validator.current.message('transferApprovalAmount', amount, 'required|amountCheck') }
              </section>
            </Col>
          </Row>
          <Row gutter={16} className="mt-2 mb-2">
            <Col span={24}>
              <Row gutter={16}>
                <Col span={18}>
                  <SpH5>Add Team Members</SpH5>
                </Col>
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="round"
                    ghost
                    onClick={handleRemoveAllMembers}
                  >
                    Remove All
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col>
              <ReactSelect
                name="transferApprovalMembers"
                isMulti
                placeholder="Select one or more Team Members"
                options={approvedMemberOptions}
                value={approvedMembers}
                onChange={option => setApprovedMembers(option)}
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option: CheckboxOption }}
                styles={tallSelectStyles}
                filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue)}
              />
              <section style={{ color: 'red' }}>
                { validator.current.message('transferApprovalMembers', approvedMembers, 'required|optionSelected') }
              </section>
            </Col>
          </Row>
        </div>
      </Spin>
    </Modal>
  );
};

const mapStateToProps = state => ({
  loading: state.loading.loading,
  selectedAccount: state.account.selectedAccount,
  test: state.account.test,
  teamMembers: state.transferApproval.teamMembers,
  existingRuleDetails: state.transferApproval.existingRuleDetails,
});

const mapDispatchToProps = dispatch => ({
  fetchTeamMembers: param => dispatch(transferApprovalActions.fetchTeamMembers({
    payload: param,
  })),
  addTransferApprovalRule: param => dispatch(transferApprovalActions.addTransferApprovalRule({
    payload: param,
  })),
  fetchExistingRuleDetail: param => dispatch(transferApprovalActions.fetchExistingRule({
    payload: param,
  })),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddRule));
